import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { WorkOrder, formatWorkOrderNumber } from "@/hooks/use-work-orders";
import { MaintenancePlan } from "@/hooks/use-maintenance-plans";
import { Edit, CheckCircle } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { formatCurrency } from "@/lib/utils";

interface OpenWorkOrdersDialogProps {
  isOpen: boolean;
  onClose: () => void;
  plan: MaintenancePlan | null;
  workOrders: WorkOrder[];
  onEditWorkOrder: (workOrder: WorkOrder) => void;
  onCloseWorkOrder: (id: string) => void;
}

const getWorkOrderStatusLabel = (status: string): string => {
  const statusLabels: Record<string, string> = {
    open: "Aberto",
    in_progress: "Em Andamento",
    closed: "Fechado",
    cancelled: "Cancelado"
  };
  return statusLabels[status] || status;
};

export function OpenWorkOrdersDialog({
  isOpen,
  onClose,
  plan,
  workOrders,
  onEditWorkOrder,
  onCloseWorkOrder,
}: OpenWorkOrdersDialogProps) {
  if (!plan) return null;

  const openOrders = workOrders.filter(
    wo => wo.plan_id === plan.id && (wo.status === 'open' || wo.status === 'in_progress')
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>OSs Abertas - {plan.name}</DialogTitle>
          <DialogDescription>
            Ordens de serviço em aberto para este plano de manutenção
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[400px] pr-4">
          <div className="space-y-3">
            {openOrders.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                Nenhuma ordem de serviço aberta para este plano.
              </div>
            ) : (
              openOrders.map((wo) => (
                <div
                  key={wo.id}
                  className="p-4 border rounded-lg space-y-2 hover:bg-accent/50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="font-mono">
                        {formatWorkOrderNumber(wo.order_number)}
                      </Badge>
                      <Badge variant={wo.status === 'open' ? 'destructive' : 'secondary'}>
                        {getWorkOrderStatusLabel(wo.status)}
                      </Badge>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onEditWorkOrder(wo)}
                      >
                        <Edit className="h-3 w-3 mr-1" />
                        Editar
                      </Button>
                      {wo.status === 'open' && (
                        <Button
                          size="sm"
                          variant="default"
                          onClick={() => onCloseWorkOrder(wo.id)}
                        >
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Fechar
                        </Button>
                      )}
                    </div>
                  </div>
                  
                  {wo.description && (
                    <p className="text-sm text-muted-foreground">
                      {wo.description}
                    </p>
                  )}
                  
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    {wo.total_cost && (
                      <span>{formatCurrency(wo.total_cost)}</span>
                    )}
                    <span>
                      {new Date(wo.opened_at).toLocaleDateString("pt-BR")}
                    </span>
                    {wo.fleet_providers && (
                      <span>{wo.fleet_providers.name}</span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </ScrollArea>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
